/** 国家选择存储 key（仅用 localStorage） */
const COUNTRY_STORAGE_KEY = "selectedCountryId";

const SET_COOKIE_PARAM = "_setCookie";
const COUNTRY_ID_PARAM = "countryId";
const FROM_DOMAIN_PARAM = "from_domain";
const FROM_COUNTRY_PARAM = "from_country";

function getParamFromUrl(param: string): string | null {
  const params = new URLSearchParams(window.location.search);
  let value = params.get(param);
  if (value) return value;
  const hash = window.location.hash.replace(/^#/, "");
  if (hash) {
    const hashParams = new URLSearchParams(hash);
    value = hashParams.get(param);
    if (value) return value;
  }
  return null;
}

function getSetCookieFromUrl(): string | null {
  return getParamFromUrl(SET_COOKIE_PARAM);
}

/**
 * 从当前 URL 读取参数并持久化：
 * - countryId / _setCookie（跳转后的国家）→ storage，供前端当前站使用；
 * - from_domain / from_country（跳转前的域名和国家）→ cookie，供后端读取。
 * 应在入口最早调用（如 main.tsx 中 initCountryAndLanguage 之前）。
 * @param setObject 用于写入 utils 存储，例如 utils.setObject
 */
export function persistCountryParamsFromUrl(
  setObject: (key: string, value: unknown) => void
): void {
  if (typeof window === "undefined") return;
  try {
    // 跳转后的国家：存 storage
    const countryIdRaw = getParamFromUrl(COUNTRY_ID_PARAM);
    const setCookieRaw = getSetCookieFromUrl();
    const countryValue = countryIdRaw ?? setCookieRaw;
    if (countryValue) {
      const decoded = decodeURIComponent(countryValue).trim();
      if (decoded) {
        localStorage.setItem(COUNTRY_STORAGE_KEY, decoded);
        setObject("countryId", decoded);
      }
    }

    // 跳转前的域名和国家：存 cookie（来自 from_domain / from_country）
    const fromDomain = getParamFromUrl(FROM_DOMAIN_PARAM);
    const fromCountry = getParamFromUrl(FROM_COUNTRY_PARAM);
    if (fromDomain && fromCountry) {
      setRedirectInfoCookie(
        decodeURIComponent(fromDomain).trim(),
        decodeURIComponent(fromCountry).trim()
      );
    }
  } catch {
    // ignore
  }
}

/**
 * 若当前 URL 带有 _setCookie 参数，则写入 localStorage 并从地址栏移除该参数（目标页落地时调用一次）
 * 建议在入口最早调用（如 main.tsx 开头），确保在路由/懒加载前执行。
 */
export function applySetCookieFromUrl(): void {
  if (typeof window === "undefined") return;
  const value = getSetCookieFromUrl();
  if (!value) return;

  const decoded = decodeURIComponent(value);

  try {
    localStorage.setItem(COUNTRY_STORAGE_KEY, decoded);
  } catch {
    // ignore
  }

  const params = new URLSearchParams(window.location.search);
  params.delete(SET_COOKIE_PARAM);
  const newSearch = params.toString();
  const newUrl = newSearch
    ? `${window.location.pathname}?${newSearch}${window.location.hash || ""}`
    : `${window.location.pathname}${window.location.hash || ""}`;
  window.history.replaceState(null, "", newUrl);
}

/** 读取当前保存的国家 ID（仅 localStorage） */
export function getCountryIdFromStorage(): string {
  try {
    const v = localStorage.getItem(COUNTRY_STORAGE_KEY);
    return v != null ? v : "";
  } catch {
    return "";
  }
}

/** 将选中国家写入 localStorage（selectedCountryId），用于“当前国家”仅存本地、不写 cookie 的场景 */
export function setCountryIdToStorage(countryId: string): void {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(COUNTRY_STORAGE_KEY, String(countryId).trim());
  } catch {
    // ignore
  }
}

/** 是否有国家选择记录（有则不再弹窗） */
export function hasCountryIdStored(): boolean {
  return getCountryIdFromStorage().trim() !== "";
}

/** 跳转前写入 cookie：当前域名、当前国家（均为跳转前的值），方便后端读取 */
const REDIRECT_FROM_DOMAIN_COOKIE = "redirect_from_domain";
const REDIRECT_COUNTRY_COOKIE = "redirect_country";
const REDIRECT_COOKIE_MAX_AGE = 60 * 60 * 24; // 1 天

export function setRedirectInfoCookie(domain: string, country: string): void {
  if (typeof document === "undefined") return;
  try {
    const opts = `path=/; max-age=${REDIRECT_COOKIE_MAX_AGE}; SameSite=Lax`;
    document.cookie = `${REDIRECT_FROM_DOMAIN_COOKIE}=${encodeURIComponent(domain)}; ${opts}`;
    document.cookie = `${REDIRECT_COUNTRY_COOKIE}=${encodeURIComponent(country)}; ${opts}`;
  } catch {
    // ignore
  }
}
